import { ClockCountdownIcon } from '@phosphor-icons/react';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';

import { cn } from '@shared/lib/utils';
import { ChartContainer, type ChartConfig } from '@shared/ui';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@shared/ui/card';
import { DropdownMenuGroup, DropdownMenuItem } from '@shared/ui/dropdown-menu';

import { CardActionMenu } from './CardActionMenu';
import { CardMetric, CardMetricSecondary, CardMetricValue } from './CardMetric';

const chartConfig: ChartConfig = {
  value: {
    label: 'Remaining',
    color: '#5EA500',
  },
};

export interface DeadlineCardProps {
  className?: string;
  remainingDays: number;
  totalDays: number;
  dueLabel: string;
  dueYear: string;
}

export const DeadlineCard = ({
  className,
  remainingDays,
  totalDays,
  dueLabel,
  dueYear,
}: DeadlineCardProps) => {
  const progress = Math.max(0, Math.min(remainingDays / Math.max(totalDays, 1), 1));
  const progressPercent = Math.round(progress * 100);
  const chartData = [{ name: 'Remaining', value: progressPercent }];
  const startAngle = 90;
  const endAngle = startAngle - 360; // clockwise sweep

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pe-4">
        <CardTitle>Deadline</CardTitle>
        <CardAction>
          <CardActionMenu ariaLabel="Open deadline actions" buttonClassName="-mt-1">
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <ClockCountdownIcon weight="bold" />
                <span>Adjust deadline</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </CardActionMenu>
        </CardAction>
      </CardHeader>

      <CardContent className="relative h-full w-full">
        <div className="relative size-full flex flex-col items-end">
          <ChartContainer
            config={chartConfig}
            className="relative aspect-square h-full translate-x-2 -translate-y-1 w-auto"
          >
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                data={chartData}
                innerRadius="83%"
                outerRadius="100%"
                startAngle={startAngle}
                endAngle={endAngle}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar
                  dataKey="value"
                  cornerRadius={999}
                  background={{ style: { fill: 'var(--color-background-4)' } }}
                  fill="var(--color-lime-500)"
                  isAnimationActive={true}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-3xl font-medium leading-none text-foreground">
                {remainingDays}
              </div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Days
              </div>
            </div>
          </ChartContainer>
          <CardMetric className="absolute bottom-0 left-0 h-full justify-end">
            <CardMetricValue className="text-nowrap">{dueLabel}</CardMetricValue>
            <CardMetricSecondary>{dueYear}</CardMetricSecondary>
          </CardMetric>
        </div>
      </CardContent>
    </Card>
  );
};
